import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { LiveNotification, RealtimeNotificationService } from '../../Service/realtime-notification.service';
import { GlobalSearchActionsService } from '../global-search/global-search-actions.service';

@Component({
  selector: 'app-live-notifications',
  templateUrl: './live-notifications.component.html',
  styleUrls: ['./live-notifications.component.scss'],
  host: { class: 'nav-item d-flex align-items-center mx-2 beell' }
})
export class LiveNotificationsComponent implements OnInit, OnDestroy {
  @Input() audience: 'admin' | 'subscriber' = 'subscriber';

  open = false;
  items: LiveNotification[] = [];
  unread = 0;
  private sub = new Subscription();

  constructor(
    private notifications: RealtimeNotificationService,
    private router: Router,
    private host: ElementRef,
    private searchActions: GlobalSearchActionsService
  ) {}

  ngOnInit() {
    this.notifications.start(this.audience, this.subscriberId());
    this.sub.add(this.notifications.items$.subscribe(items => this.items = items));
    this.sub.add(this.notifications.unread$.subscribe(count => this.unread = count));
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
    this.notifications.stop();
  }

  toggle(event: Event) {
    event.stopPropagation();
    this.open = !this.open;
  }

  @HostListener('document:click', ['$event'])
  closeOnOutside(event: MouseEvent) {
    if (this.open && !this.host.nativeElement.contains(event.target)) {
      this.open = false;
    }
  }

  openItem(item: LiveNotification) {
    this.notifications.markRead(item);
    this.open = false;
    // Prefer the record named in the message; the API link is often just the module page.
    const message = item.message || '';
    const appId = message.match(/Application\s+#?(\d+)/i);
    const blocked = message.match(/^(.+?)\s+was blocked/i);
    if (appId && this.audience === 'subscriber') {
      this.router.navigate(['/subscriber-reports/report-list'], { queryParams: { id: appId[1] } });
    } else if (/invoice|payment/i.test(message) && this.audience === 'subscriber') {
      this.router.navigate(['/subscriber-manage-invoice']);
    } else if (appId) {
      // Finds whichever queue (QA / Errored / Completed) holds the application.
      this.searchActions.openInQueue(appId[1], '/qa');
    } else if (blocked) {
      this.router.navigate(['/subscribers/manage-subscriber'], { queryParams: { searchKey: blocked[1].trim() } });
    } else if (item.link) {
      // Strip origin / "#" so full URLs and hash links both work with the router.
      this.router.navigateByUrl(item.link.replace(/^https?:\/\/[^/]+/i, '').replace(/^\/?#/, ''));
    }
  }

  markAll(event: Event) {
    event.stopPropagation();
    this.notifications.markAllRead();
  }

  viewAll(event: Event) {
    event.stopPropagation();
    this.open = false;
    this.router.navigate(['/alert-notification']);
  }

  when(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleString();
  }

  unreadItem(item: LiveNotification): boolean {
    return !this.notifications.isRead(item);
  }

  get badgeLevel(): 'low' | 'mid' | 'high' {
    if (this.unread < 5) {
      return 'low';
    }
    if (this.unread <= 10) {
      return 'mid';
    }
    return 'high';
  }

  private subscriberId(): number | null {
    if (this.audience !== 'subscriber') {
      return null;
    }
    const session = JSON.parse(sessionStorage.getItem('subscriberLogin') || 'null');
    if (!session || !session.Data) {
      return null;
    }
    if (session.token && session.Data.subscriberId) {
      return Number(session.Data.subscriberId);
    }
    return Number(session.Data.id) || null;
  }
}
